import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  GET_GPS_OFFICE_LOCATIONS,
  UPDATE_GPS_OFFICE_LOCATIONS,
} from '../../graphql/gps-settings';

const GET_GOOGLE_MAPS_API_KEY = gql`
  query GetGoogleMapsApiKey {
    getGoogleMapsApiKey
  }
`;

const UPDATE_GOOGLE_MAPS_API_KEY = gql`
  mutation UpdateGoogleMapsApiKey($apiKey: String!) {
    updateGoogleMapsApiKey(apiKey: $apiKey)
  }
`;

const GEOCODE_ALL_PROPERTIES = gql`
  mutation GeocodeAllProperties($batchSize: Int) {
    geocodeAllProperties(batchSize: $batchSize) {
      geocodedCount
      message
    }
  }
`;

const EMPTY_OFFICE = { name: '', latitude: '', longitude: '' };
const MAX_OFFICE_LOCATIONS = 3;

const buildOfficeFormState = (locations) => {
  const offices = Array.from({ length: MAX_OFFICE_LOCATIONS }, () => ({ ...EMPTY_OFFICE }));
  if (!locations?.length) {
    return offices;
  }
  locations.slice(0, MAX_OFFICE_LOCATIONS).forEach((office, index) => {
    offices[index] = {
      name: office.name || '',
      latitude: office.latitude != null ? String(office.latitude) : '',
      longitude: office.longitude != null ? String(office.longitude) : '',
    };
  });
  return offices;
};

function GPSSettingsSection() {
  const [apiKey, setApiKey] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState(null);
  const [offices, setOffices] = useState(() => buildOfficeFormState());
  const [isEditingOffices, setIsEditingOffices] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_GOOGLE_MAPS_API_KEY, {
    fetchPolicy: 'cache-and-network',
  });

  const [updateApiKey, { loading: updateLoading }] = useMutation(UPDATE_GOOGLE_MAPS_API_KEY, {
    onCompleted: () => {
      toast.success('Google Maps API key updated successfully');
      setIsEditing(false);
      refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update API key: ${error.message}`);
    },
  });

  const {
    data: officeData,
    loading: officesLoading,
    refetch: refetchOffices,
  } = useQuery(GET_GPS_OFFICE_LOCATIONS, {
    fetchPolicy: 'cache-and-network',
  });

  const [updateOfficeLocations, { loading: updateOfficesLoading }] = useMutation(
    UPDATE_GPS_OFFICE_LOCATIONS,
    {
      onCompleted: () => {
        toast.success('Office locations updated successfully');
        setIsEditingOffices(false);
        refetchOffices();
      },
      onError: (mutationError) => {
        toast.error(`Failed to update office locations: ${mutationError.message}`);
      },
    },
  );

  const [geocodeAllProperties, { loading: geocodeLoading }] = useMutation(GEOCODE_ALL_PROPERTIES, {
    onCompleted: (data) => {
      const { geocodedCount, message } = data.geocodeAllProperties;
      toast.success(message || `Successfully geocoded ${geocodedCount} properties`);
      setIsGeocoding(false);
      setGeocodingProgress(null);
    },
    onError: (error) => {
      toast.error(`Failed to geocode properties: ${error.message}`);
      setIsGeocoding(false);
      setGeocodingProgress(null);
    },
  });

  useEffect(() => {
    if (data?.getGoogleMapsApiKey) {
      setApiKey(data.getGoogleMapsApiKey);
    }
  }, [data]);

  useEffect(() => {
    if (!isEditingOffices && officeData?.getGpsOfficeLocations) {
      setOffices(buildOfficeFormState(officeData.getGpsOfficeLocations));
    }
  }, [officeData, isEditingOffices]);

  const handleSaveApiKey = () => {
    if (!apiKey.trim()) {
      toast.error('Please enter a valid API key');
      return;
    }

    // Extract the actual key if it's masked (contains ...)
    // If user is editing, they should paste the full key
    // We'll save whatever they paste
    updateApiKey({
      variables: {
        apiKey: apiKey.trim(),
      },
    });
  };

  const handleGeocodeAll = () => {
    if (!window.confirm('This will geocode all properties without coordinates. This may take several minutes. Continue?')) {
      return;
    }

    setIsGeocoding(true);
    setGeocodingProgress('Starting geocoding process...');

    geocodeAllProperties({
      variables: {
        batchSize: 10,
      },
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setShowApiKey(false);
    // Reset to original masked value
    if (data?.getGoogleMapsApiKey) {
      setApiKey(data.getGoogleMapsApiKey);
    } else {
      setApiKey('');
    }
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setShowApiKey(true);
    // Clear the field so user can paste full key
    setApiKey('');
  };

  const handleOfficeFieldChange = (index, field, value) => {
    setOffices((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSaveOffices = () => {
    const locations = [];
    for (let index = 0; index < offices.length; index += 1) {
      const office = offices[index];
      const name = office.name.trim();
      const latStr = String(office.latitude).trim();
      const lngStr = String(office.longitude).trim();
      const isEmpty = !name && !latStr && !lngStr;
      if (isEmpty) {
        continue;
      }
      if (!name) {
        toast.error(`Office ${index + 1}: name is required`);
        return;
      }
      const latitude = parseFloat(latStr);
      const longitude = parseFloat(lngStr);
      if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
        toast.error(`Office ${index + 1}: valid latitude and longitude are required`);
        return;
      }
      locations.push({ name, latitude, longitude });
    }
    if (locations.length > MAX_OFFICE_LOCATIONS) {
      toast.error(`At most ${MAX_OFFICE_LOCATIONS} office locations are allowed`);
      return;
    }
    updateOfficeLocations({ variables: { locations } });
  };

  const handleCancelOfficeEdit = () => {
    setIsEditingOffices(false);
    setOffices(buildOfficeFormState(officeData?.getGpsOfficeLocations));
  };

  if (loading || officesLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">Loading GPS settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">GPS Tracking Settings</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure Google Maps API for geocoding property addresses to GPS coordinates
        </p>
      </div>

      <div className="px-6 py-6 space-y-6">
        {/* API Key Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Google Maps API Key</h3>
              <p className="mt-1 text-sm text-gray-500">
                API key used for geocoding property addresses. Stored in database for easy management.
              </p>
            </div>
            {!isEditing && (
              <button
                onClick={handleStartEdit}
                className="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {apiKey ? 'Update' : 'Set'} API Key
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="api-key" className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <input
                  type={showApiKey ? 'text' : 'password'}
                  id="api-key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter Google Maps API key"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <div className="mt-2 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    {showApiKey ? 'Hide' : 'Show'} key
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSaveApiKey}
                  disabled={updateLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={updateLoading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <code className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-mono text-gray-800">
                  {apiKey || 'Not configured'}
                </code>
                {apiKey && (
                  <span className="text-sm text-green-600 flex items-center">
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Configured
                  </span>
                )}
              </div>
              {!apiKey && (
                <p className="mt-2 text-sm text-yellow-600">
                  API key is not configured. Geocoding will not work until an API key is set.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Office Locations Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Office Locations</h3>
              <p className="mt-1 text-sm text-gray-500">
                Configure up to three office coordinates. Timeclock punches and GPS map locations are matched to these offices.
              </p>
            </div>
            {!isEditingOffices && (
              <button
                type="button"
                onClick={() => setIsEditingOffices(true)}
                className="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                aria-label="Edit office locations"
              >
                Edit Offices
              </button>
            )}
          </div>

          {isEditingOffices ? (
            <div className="space-y-6">
              {offices.map((office, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-3">
                  <h4 className="text-sm font-medium text-gray-800">Office {index + 1}</h4>
                  <div>
                    <label htmlFor={`office-name-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      id={`office-name-${index}`}
                      type="text"
                      value={office.name}
                      onChange={(e) => handleOfficeFieldChange(index, 'name', e.target.value)}
                      placeholder="e.g. Main Office"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor={`office-lat-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Latitude
                      </label>
                      <input
                        id={`office-lat-${index}`}
                        type="number"
                        step="any"
                        value={office.latitude}
                        onChange={(e) => handleOfficeFieldChange(index, 'latitude', e.target.value)}
                        placeholder="32.610000"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor={`office-lng-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Longitude
                      </label>
                      <input
                        id={`office-lng-${index}`}
                        type="number"
                        step="any"
                        value={office.longitude}
                        onChange={(e) => handleOfficeFieldChange(index, 'longitude', e.target.value)}
                        placeholder="-80.080000"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-500">Leave all offices blank to clear office detection. Partial rows are ignored.</p>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={handleSaveOffices}
                  disabled={updateOfficesLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {updateOfficesLoading ? 'Saving...' : 'Save Offices'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelOfficeEdit}
                  disabled={updateOfficesLoading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {(officeData?.getGpsOfficeLocations?.length ?? 0) === 0 ? (
                <p className="text-sm text-gray-500">No office locations configured.</p>
              ) : (
                officeData.getGpsOfficeLocations.map((office) => (
                  <div key={office.name} className="text-sm text-gray-700 font-mono bg-gray-50 border border-gray-200 rounded px-3 py-2">
                    <span className="font-sans font-medium text-gray-900">{office.name}</span>
                    {' — '}
                    {office.latitude}, {office.longitude}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Geocode Properties Section */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-2">Geocode Properties</h3>
          <p className="text-sm text-gray-500 mb-4">
            Update all properties with latitude/longitude coordinates based on their addresses. This process may take several minutes for large numbers of properties.
          </p>

          <button
            onClick={handleGeocodeAll}
            disabled={isGeocoding || geocodeLoading || !apiKey}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeocoding || geocodeLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Geocoding properties...
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Geocode All Properties
              </>
            )}
          </button>

          {geocodingProgress && (
            <p className="mt-2 text-sm text-gray-600">{geocodingProgress}</p>
          )}

          {!apiKey && (
            <p className="mt-2 text-sm text-yellow-600">
              Please configure the Google Maps API key above before geocoding properties.
            </p>
          )}
        </div>

        {/* Info Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">About GPS Geocoding</h4>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Geocoding converts property addresses to GPS coordinates (latitude/longitude)</li>
                  <li>Coordinates are used to verify technician locations when starting/stopping work</li>
                  <li>The API key is stored in the database for easy management across environments</li>
                  <li>If no API key is in the database, the system falls back to the environment variable</li>
                  <li>Geocoding processes properties in batches to respect API rate limits</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GPSSettingsSection;
